import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  ButtonPrimary,
  ButtonSecondary,
  SidePanelType,
  usePrevious,
  responseErrorsResolver,
  ResponseError,
  Popup,
  PermissionEnum,
  ButtonPseudo,
  PermissionDenied,
  ajvResolver,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  NewApplicationValues,
  NewApplicationListItem,
} from '../new-application/types';
import { defaultFormValues, changePanelState } from './store/slices/create';
import { setHintVisible } from './store/slices/list';
import CreateForm from '../../components/individual/create/create';
import { useCreateApplicationMutation } from '../new-application/store/services';
import { useLazyGetCustomersInfoDictionariesQuery } from '../../store/dictionaries/services';
import { ApplicationScheme, ApplicationSchemeType } from '../customers/types';

import ROUTES from '../../routes';

import cn from './information.module.css';

const schema = {
  type: 'object',
  properties: {
    firstName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'First Name is required',
    },
    lastName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Last Name is required',
    },
    phone: { type: 'string', minLength: 1, errorMessage: 'Phone is required' },
    email: { type: 'string', minLength: 1, errorMessage: 'E-mail is required' },
    dob: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Date of Birth is required',
    },
    nationalityId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Nationality is required',
    },
    deliveryChannelId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Delivery Channel is required',
    },
  },
  required: [
    'firstName',
    'lastName',
    'phone',
    'dob',
    'email',
    'nationalityId',
    'deliveryChannelId',
  ],
};

const IndividualCustomerCreate = (): React.JSX.Element => {
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [sumSubError, setSumSubError] = useState('');
  const [formattedSchema, setFormattedSchema] = useState(schema);
  const [selectedSchema, setSelectedSchema] = useState<
    ApplicationScheme | undefined
  >();

  const {
    individualCustomers: {
      create: { panelIsOpen, submitSuccess },
    },
    customersDictionaries: {
      activePermissions,
      applicationSchemes,
      deliveryChannelsIndividual,
    },
  } = useAppSelector((state) => state);

  const sidepanelRef = useRef<HTMLDivElement | null>(null);

  const [getInfoDictionaries] = useLazyGetCustomersInfoDictionariesQuery();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setError,
    watch,
  } = useForm<NewApplicationValues>({
    defaultValues: defaultFormValues,
    resolver: ajvResolver(formattedSchema),
  });

  const email = watch('email');

  const prevEmail = usePrevious(email);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const handleClose = () => {
    dispatch(changePanelState(false));

    reset(defaultFormValues);

    setTimeout(() => {
      navigate(`${ROUTES.CUSTOMERS_INDIVIDUAL_LIST}${search || ''}`);
    }, 100);
  };

  const [create, { isLoading: loading }] = useCreateApplicationMutation();

  const onSubmit = handleSubmit(async (values) => {
    setSumSubError('');
    setErrorMessage('');

    const dob = new Date(values?.dob);

    const body: NewApplicationValues = {
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      email: values?.email,
      dob: `${dob.getFullYear()}-${(dob.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${dob.getDate().toString().padStart(2, '0')}`,
      isUSResident: values.isUSResident || false,
      participatesInPolitics: values.participatesInPolitics || false,
    };

    if (values?.applicationSchemeId) {
      body.applicationSchemeId = values.applicationSchemeId;
    }

    if (values.applicantId) {
      body.applicantId = values.applicantId;
    }

    if (values.nationalityId) {
      body.nationalityId = values.nationalityId;
    }

    if (values.deliveryChannelId) {
      body.deliveryChannelId = values.deliveryChannelId;
    }

    if (
      selectedSchema &&
      selectedSchema.deliveryChannelVisible &&
      !values.deliveryChannelId
    ) {
      setError('deliveryChannelId', {
        type: 'custom',
        message: 'Delivery Channel is required',
      });
    } else {
      const response = (await create(body)) as {
        data?: { data: NewApplicationListItem };
        error?: ResponseError;
      };

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError: setErrorMessage,
        });

        if (
          response.error &&
          response.error.data &&
          response.error.data.message
        ) {
          if (
            Array.isArray(response.error.data.message) &&
            response.error.data.message[0] &&
            typeof response.error.data.message[0] === 'string' &&
            (response.error.data.message[0] as string).includes('applicant')
          ) {
            setSumSubError(response.error.data.message[0]);
          }
        }

        const offsetsArray: number[] = [];

        if (Array.isArray(response?.error?.data.message)) {
          response?.error?.data.message.forEach((item) => {
            const offset = document.getElementById(item.property)?.offsetTop;

            if (offset) {
              offsetsArray.push(offset);
            }
          });
        }

        if (sidepanelRef.current) {
          sidepanelRef.current.scroll(
            0,
            offsetsArray.sort()[0] - (sidepanelRef.current?.offsetTop || 0),
          );
        }
      }
    }
  });

  useEffect(() => {
    if (submitSuccess) {
      dispatch(changePanelState(false));

      setTimeout(() => {
        dispatch(setHintVisible(true));
      }, 100);
    }
  }, [dispatch, submitSuccess]);

  useEffect(() => {
    dispatch(changePanelState(true));
  }, []);

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_CREATE_ACTION,
      )
    ) {
      getInfoDictionaries();
    }
  }, [activePermissions]);

  useEffect(() => {
    let nextSchema: any = schema;

    if (email) {
      nextSchema = {
        ...schema,
        properties: {
          ...schema.properties,
          email: {
            type: 'string',
            minLength: 1,
            format: 'email',
            errorMessage: 'Email is empty or invalid',
          },
        },
      };
    } else if (!email && prevEmail) {
      nextSchema = schema;
    }

    setFormattedSchema(nextSchema);
  }, [email, prevEmail]);

  let content = (
    <CreateForm
      control={control}
      errors={errors}
      sumSubError={sumSubError}
      applicationSchemes={applicationSchemes?.filter(
        (item) => item.type === ApplicationSchemeType.INDIVIDUAL,
      )}
      activePermissions={activePermissions}
      setSelectedSchema={setSelectedSchema}
      deliveryChannelsIndividual={deliveryChannelsIndividual}
    />
  );

  if (
    !activePermissions?.includes(
      PermissionEnum.INDIVIDUAL_CUSTOMERS_CREATE_ACTION,
    )
  ) {
    content = <PermissionDenied />;
  }

  let footerContent = (
    <div className={cn.sidePanelFooterWrapper}>
      <ButtonPrimary
        text={loading ? 'Loading...' : 'Create application'}
        onClick={onSubmit}
        bold
        disabled={loading}
      />
      <ButtonSecondary text="Cancel" onClick={handleClose} bold />
      {Object.keys(errors)?.length > 0 && (
        <div className={cn.formError}>There are errors in some fields</div>
      )}
    </div>
  );

  if (
    !activePermissions?.includes(
      PermissionEnum.INDIVIDUAL_CUSTOMERS_CREATE_ACTION,
    )
  ) {
    footerContent = <ButtonPseudo text="Close" onClick={handleClose} bold />;
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Customers}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title="Create Individual customer"
        childrenRef={sidepanelRef}
        footer={footerContent}
        errorSidepanel={errorMessage}
        sidepanelExpandedKey="create-individual"
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default IndividualCustomerCreate;
